import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, ShoppingCart, TrendingUp, Plus, Edit, Trash2, Eye, X, Check, Upload, ImageIcon, Tag, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  pending: 'text-yellow-400', processing: 'text-blue-400',
  shipped: 'text-primary', delivered: 'text-green-400', cancelled: 'text-red-400'
};

const TABS = ['dashboard', 'products', 'orders', 'coupons', 'settings'];

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, token, isAdmin, getHeaders, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [productForm, setProductForm] = useState(null); // null = closed, {} = new, {id} = edit
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [coupons, setCoupons] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const csvRef = useRef();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    if (!isAdmin) { navigate('/'); toast.error('Admin access required'); return; }
    loadData();
  }, [user, isAdmin, authLoading, activeTab, page, orderSearch, orderStatusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const [statsRes, lowRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: getHeaders() }),
          axios.get(`${API}/admin/low-stock`, { headers: getHeaders() }),
        ]);
        setStats(statsRes.data);
        setLowStock(lowRes.data.products || []);
      } else if (activeTab === 'products') {
        const res = await axios.get(`${API}/admin/products?page=${page}&limit=15`, { headers: getHeaders() });
        setProducts(res.data.products || []);
        setTotalPages(res.data.pages || 1);
      } else if (activeTab === 'orders') {
        const params = new URLSearchParams({ page, limit: 15 });
        if (orderStatusFilter) params.set('status', orderStatusFilter);
        if (orderSearch.trim()) params.set('search', orderSearch.trim());
        const res = await axios.get(`${API}/admin/orders?${params}`, { headers: getHeaders() });
        setOrders(res.data.orders || []);
        setTotalPages(res.data.pages || 1);
      } else if (activeTab === 'coupons') {
        const res = await axios.get(`${API}/admin/coupons`, { headers: getHeaders() });
        setCoupons(res.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const [syncing, setSyncing] = useState(false);

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/admin/products/import-csv`, fd, { headers: getHeaders() });
      const d = res.data;
      toast.success(`Import done: ${d.created} created, ${d.updated} updated`);
      if (d.errors?.length) toast.error(`${d.errors.length} rows had errors`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'CSV import failed'); }
    finally { setCsvImporting(false); if (csvRef.current) csvRef.current.value = ''; }
  };

  const syncImages = async () => {
    if (!window.confirm('This will update all product images to the latest catalog images. Continue?')) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sync-images`, {}, { headers: getHeaders() });
      toast.success(`Images synced: ${res.data.updated} products updated`);
      loadData();
    } catch { toast.error('Image sync failed'); }
    finally { setSyncing(false); }
  };

  const deleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: getHeaders() });
      toast.success('Order deleted');
      loadData();
    } catch { toast.error('Failed to delete order'); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, { headers: getHeaders() });
      toast.success('Order status updated');
      setEditingOrder(null);
      loadData();
    } catch { toast.error('Failed to update status'); }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { headers: getHeaders() });
      toast.success('Product deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const saveProduct = async (formData) => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
        uae_market_price: formData.uae_market_price ? parseFloat(formData.uae_market_price) : null,
        stock: parseInt(formData.stock) || 0,
        featured: formData.featured === true || formData.featured === 'true',
        images: formData.images ? (typeof formData.images === 'string' ? formData.images.split('\n').filter(Boolean) : formData.images) : [],
        features: formData.features ? (typeof formData.features === 'string' ? formData.features.split('\n').filter(Boolean) : formData.features) : [],
        tags: formData.tags ? (typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : formData.tags) : [],
        specs: typeof formData.specs === 'string' ? JSON.parse(formData.specs || '{}') : (formData.specs || {}),
        avg_rating: parseFloat(formData.avg_rating) || 0,
        reviews_count: parseInt(formData.reviews_count) || 0,
      };
      if (formData.id) {
        await axios.put(`${API}/products/${formData.id}`, data, { headers: getHeaders() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data, { headers: getHeaders() });
        toast.success('Product created');
      }
      setProductForm(null);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save product'); }
    finally { setSaving(false); }
  };

  if (!user || !isAdmin) return null;

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        {/* Admin header */}
        <div className="bg-surface border-b border-white/5 py-4">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading font-black text-2xl uppercase text-white">Admin Panel</h1>
                <p className="text-xs text-muted-foreground">M N M Tools Hub Management</p>
              </div>
              {/* Tabs */}
              <div className="flex gap-1">
                {TABS.map(tab => (
                  <button
                    key={tab}
                    onClick={() => { setActiveTab(tab); setPage(1); }}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === tab ? 'bg-primary text-black' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
                    data-testid={`admin-tab-${tab}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-6">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div data-testid="admin-dashboard">
              {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[1,2,3,4].map(i => <div key={i} className="h-24 shimmer-bg" />)}
                </div>
              ) : stats && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                      { icon: Package, label: 'Total Products', value: stats.total_products, color: 'text-primary' },
                      { icon: ShoppingCart, label: 'Total Orders', value: stats.total_orders, color: 'text-secondary' },
                      { icon: Users, label: 'Total Users', value: stats.total_users, color: 'text-blue-400' },
                      { icon: TrendingUp, label: 'Total Revenue', value: `AED ${stats.total_revenue?.toFixed(0)}`, color: 'text-green-400' },
                    ].map(({ icon: Icon, label, value, color }) => (
                      <div key={label} className="bg-surface border border-white/5 p-5">
                        <div className="flex items-center justify-between mb-3">
                          <Icon size={18} className={color} />
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
                        </div>
                        <div className={`font-heading font-black text-2xl ${color}`}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Order status breakdown */}
                  {stats.order_statuses && Object.keys(stats.order_statuses).length > 0 && (
                    <div className="bg-surface border border-white/5 p-5 mb-6">
                      <h3 className="font-heading font-bold text-sm uppercase text-white mb-4">Order Status Breakdown</h3>
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(stats.order_statuses).map(([status, count]) => (
                          <div key={status} className={`px-3 py-2 border ${STATUS_COLORS[status] ? `border-current bg-current/5 ${STATUS_COLORS[status]}` : 'border-white/10 text-white/50'}`}>
                            <span className="text-xs font-bold uppercase">{status}: {count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent orders */}
                  {stats.recent_orders?.length > 0 && (
                    <div className="bg-surface border border-white/5 p-5">
                      <h3 className="font-heading font-bold text-sm uppercase text-white mb-4">Recent Orders</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-3 text-muted-foreground uppercase tracking-wider">Order</th>
                              <th className="text-left py-2 px-3 text-muted-foreground uppercase tracking-wider">Customer</th>
                              <th className="text-left py-2 px-3 text-muted-foreground uppercase tracking-wider">Items</th>
                              <th className="text-right py-2 px-3 text-muted-foreground uppercase tracking-wider">Total</th>
                              <th className="text-center py-2 px-3 text-muted-foreground uppercase tracking-wider">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.recent_orders.map(order => (
                              <tr key={order.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                                <td className="py-2 px-3 font-mono text-primary">{order.order_number}</td>
                                <td className="py-2 px-3 text-white">{order.user_name}</td>
                                <td className="py-2 px-3 text-white/60">{order.items?.length} items</td>
                                <td className="py-2 px-3 text-right font-mono font-bold text-primary">AED {order.total?.toFixed(0)}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`font-bold capitalize ${STATUS_COLORS[order.status] || 'text-white/60'}`}>{order.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {/* Low Stock Alert */}
                  {lowStock.length > 0 && (
                    <div className="bg-surface border border-yellow-400/20 p-5 mt-6">
                      <h3 className="font-heading font-bold text-sm uppercase text-yellow-400 mb-4 flex items-center gap-2">
                        <AlertTriangle size={14} /> Low Stock Alert ({lowStock.length} products)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead><tr className="border-b border-white/10">
                            <th className="text-left py-2 px-3 text-muted-foreground uppercase">Product</th>
                            <th className="text-left py-2 px-3 text-muted-foreground uppercase">SKU</th>
                            <th className="text-left py-2 px-3 text-muted-foreground uppercase">Category</th>
                            <th className="text-right py-2 px-3 text-muted-foreground uppercase">Stock</th>
                          </tr></thead>
                          <tbody>
                            {lowStock.map(p => (
                              <tr key={p.id} className="border-b border-white/5">
                                <td className="py-2 px-3 text-white">{p.name}</td>
                                <td className="py-2 px-3 font-mono text-white/60">{p.sku}</td>
                                <td className="py-2 px-3 text-white/60">{p.category}</td>
                                <td className={`py-2 px-3 text-right font-bold font-mono ${p.stock === 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                                  {p.stock === 0 ? 'OUT' : p.stock}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Products */}
          {activeTab === 'products' && (
            <div data-testid="admin-products">
              <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleCsvImport} />
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-lg uppercase text-white">Products ({products.length})</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => csvRef.current?.click()}
                    disabled={csvImporting}
                    className="flex items-center gap-1.5 border border-white/10 hover:border-primary text-white/60 hover:text-primary text-xs font-bold uppercase px-3 py-2 transition-colors disabled:opacity-50"
                    data-testid="import-csv-btn"
                    title="Import products from CSV"
                  >
                    <FileSpreadsheet size={13} /> {csvImporting ? 'Importing...' : 'Import CSV'}
                  </button>
                  <a
                    href={`${API}/admin/products/csv-template`}
                    download="mnm_tools_import_template.csv"
                    className="flex items-center gap-1.5 border border-white/10 hover:border-green-500 text-white/60 hover:text-green-400 text-xs font-bold uppercase px-3 py-2 transition-colors"
                    data-testid="download-csv-template-btn"
                    title="Download sample CSV template"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const res = await axios.get(`${API}/admin/products/csv-template`, { headers: getHeaders(), responseType: 'blob' });
                        const url = window.URL.createObjectURL(res.data);
                        const a = document.createElement('a'); a.href = url; a.download = 'mnm_tools_import_template.csv'; a.click();
                        window.URL.revokeObjectURL(url);
                      } catch { toast.error('Failed to download template'); }
                    }}
                  >
                    <FileSpreadsheet size={13} /> Sample CSV
                  </a>
                  <button
                    onClick={syncImages}
                    disabled={syncing}
                    className="flex items-center gap-1.5 border border-white/10 hover:border-primary text-white/60 hover:text-primary text-xs font-bold uppercase px-3 py-2 transition-colors disabled:opacity-50"
                    data-testid="sync-images-btn"
                    title="Sync all product images to latest catalog images"
                  >
                    {syncing ? 'Syncing...' : 'Sync Images'}
                  </button>
                  <button
                    onClick={() => setProductForm({ sku: '', name: '', brand: '', category: '', description: '', price: '', compare_price: '', uae_market_price: '', stock: '', images: '', features: '', tags: '', specs: '{}', featured: false, power_type: 'corded', voltage: '220V', warranty: '1 Year' })}
                    className="flex items-center gap-1.5 bg-primary text-black text-xs font-bold uppercase px-4 py-2 hover:bg-white transition-colors"
                    data-testid="add-product-btn"
                  >
                    <Plus size={13} /> Add Product
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-12 shimmer-bg" />)}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="products-table">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Product</th>
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Brand</th>
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Category</th>
                        <th className="text-right py-2 px-3 text-muted-foreground uppercase">Price</th>
                        <th className="text-center py-2 px-3 text-muted-foreground uppercase">Stock</th>
                        <th className="text-center py-2 px-3 text-muted-foreground uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(p => (
                        <tr key={p.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-8 h-8 object-cover flex-shrink-0" />}
                              <div>
                                <p className="text-white font-semibold line-clamp-1">{p.name}</p>
                                <p className="text-muted-foreground font-mono">{p.sku}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-primary font-bold">{p.brand}</td>
                          <td className="py-2 px-3 text-white/60">{p.category}</td>
                          <td className="py-2 px-3 text-right font-mono text-primary font-bold">AED {p.price}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`font-bold ${p.stock > 10 ? 'text-green-400' : p.stock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>{p.stock}</span>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => navigate(`/products/${p.id}`)} className="text-white/40 hover:text-primary transition-colors" data-testid={`view-product-${p.id}`}><Eye size={13} /></button>
                              <button onClick={() => setProductForm({ ...p, images: Array.isArray(p.images) ? p.images.join('\n') : '', features: Array.isArray(p.features) ? p.features.join('\n') : '', tags: Array.isArray(p.tags) ? p.tags.join(', ') : '', specs: typeof p.specs === 'object' ? JSON.stringify(p.specs, null, 2) : (p.specs || '{}') })} className="text-white/40 hover:text-primary transition-colors" data-testid={`edit-product-${p.id}`}><Edit size={13} /></button>
                              <button onClick={() => deleteProduct(p.id)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-product-${p.id}`}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 text-xs font-bold transition-colors ${page === p ? 'bg-primary text-black' : 'bg-surface border border-white/10 text-white hover:border-primary'}`}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders */}
          {activeTab === 'orders' && (
            <div data-testid="admin-orders">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h2 className="font-heading font-bold text-lg uppercase text-white">All Orders</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    placeholder="Search order # or customer..."
                    value={orderSearch}
                    onChange={e => { setOrderSearch(e.target.value); setPage(1); }}
                    className="bg-surface border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-primary/50 w-52 placeholder:text-white/30"
                    style={{ borderRadius: 0 }}
                    data-testid="order-search-input"
                  />
                  <select
                    value={orderStatusFilter}
                    onChange={e => { setOrderStatusFilter(e.target.value); setPage(1); }}
                    className="bg-surface border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-primary/50"
                    style={{ borderRadius: 0 }}
                    data-testid="order-status-filter"
                  >
                    <option value="">All Statuses</option>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                  {(orderSearch || orderStatusFilter) && (
                    <button
                      onClick={() => { setOrderSearch(''); setOrderStatusFilter(''); setPage(1); }}
                      className="text-xs text-white/40 hover:text-white/80 border border-white/10 px-2 py-2 transition-colors"
                      data-testid="clear-order-filters-btn"
                      title="Clear filters"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-12 shimmer-bg" />)}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Order #</th>
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Customer</th>
                        <th className="text-left py-2 px-3 text-muted-foreground uppercase">Date</th>
                        <th className="text-right py-2 px-3 text-muted-foreground uppercase">Total</th>
                        <th className="text-center py-2 px-3 text-muted-foreground uppercase">Payment</th>
                        <th className="text-center py-2 px-3 text-muted-foreground uppercase">Status</th>
                        <th className="text-center py-2 px-3 text-muted-foreground uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                          <td className="py-2 px-3 font-mono text-primary">{order.order_number}</td>
                          <td className="py-2 px-3 text-white">{order.user_name}<br /><span className="text-muted-foreground">{order.user_email}</span></td>
                          <td className="py-2 px-3 text-white/60">{new Date(order.created_at).toLocaleDateString()}</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-primary">AED {order.total?.toFixed(0)}</td>
                          <td className="py-2 px-3 text-center text-white/60 uppercase">{order.payment_method}</td>
                          <td className="py-2 px-3 text-center">
                            {editingOrder === order.id ? (
                              <div className="flex items-center gap-1 justify-center">
                                <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="bg-background border border-white/20 text-white text-xs px-1 py-0.5 outline-none" style={{ borderRadius: 0 }}>
                                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button onClick={() => updateOrderStatus(order.id, newStatus)} className="text-green-400 hover:text-green-300"><Check size={12} /></button>
                                <button onClick={() => setEditingOrder(null)} className="text-red-400 hover:text-red-300"><X size={12} /></button>
                              </div>
                            ) : (
                              <span className={`font-bold capitalize cursor-pointer hover:underline ${STATUS_COLORS[order.status] || 'text-white/60'}`}
                                onClick={() => { setEditingOrder(order.id); setNewStatus(order.status); }}
                                data-testid={`order-status-${order.id}`}>
                                {order.status}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => navigate(`/order-success/${order.id}`)} className="text-white/40 hover:text-primary transition-colors" title="View order" data-testid={`view-order-${order.id}`}><Eye size={13} /></button>
                              <button onClick={() => deleteOrder(order.id, order.order_number)} className="text-white/40 hover:text-red-400 transition-colors" title="Delete order" data-testid={`delete-order-${order.id}`}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Coupons */}
          {activeTab === 'coupons' && (
            <CouponsSection coupons={coupons} getHeaders={getHeaders} reload={loadData} />
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <ChangePasswordSection token={token} getHeaders={getHeaders} />
          )}
        </div>
      </div>

      {/* Product Form Modal */}
      {productForm !== null && (
        <ProductFormModal
          form={productForm}
          onClose={() => setProductForm(null)}
          onSave={saveProduct}
          saving={saving}
          token={token}
        />
      )}
    </div>
  );
}

function CouponsSection({ coupons, getHeaders, reload }) {
  const EMPTY = { code: '', type: 'percent', value: '', description: '', active: true };
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // code of coupon being edited
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openEdit = (c) => { setForm({ ...c, value: c.value }); setEditing(c.code); };
  const cancelEdit = () => { setForm(EMPTY); setEditing(null); };

  const save = async () => {
    if (!form.code.trim() || !form.value) { toast.error('Code and value are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coupons/${editing}`, form, { headers: getHeaders() });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coupons`, form, { headers: getHeaders() });
        toast.success('Coupon created');
      }
      setForm(EMPTY); setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save coupon'); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coupons/${c.code}`, { ...c, active: !c.active }, { headers: getHeaders() });
      toast.success(c.active ? 'Coupon deactivated' : 'Coupon activated'); reload();
    } catch { toast.error('Failed to update coupon'); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coupons/${code}`, { headers: getHeaders() });
      toast.success('Coupon deleted'); reload();
    } catch { toast.error('Failed to delete coupon'); }
  };

  return (
    <div data-testid="admin-coupons">
      <h2 className="font-heading font-bold text-lg uppercase text-white mb-6">Coupon Manager</h2>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="bg-surface border border-white/10 p-5">
          <h3 className="font-bold text-sm uppercase tracking-widest text-white mb-4">
            {editing ? `Edit: ${editing}` : 'New Coupon'}
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Code</label>
              <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} disabled={!!editing}
                className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 text-sm outline-none uppercase disabled:opacity-50" style={{borderRadius:0}}
                placeholder="SAVE10" data-testid="coupon-code-input" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Type</label>
              <select value={form.type} onChange={e => set('type', e.target.value)}
                className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 text-sm outline-none" style={{borderRadius:0}}>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed Amount (AED)</option>
                <option value="shipping">Free Shipping</option>
              </select>
            </div>
            {form.type !== 'shipping' && (
              <div>
                <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">
                  {form.type === 'percent' ? 'Discount %' : 'Amount (AED)'}
                </label>
                <input type="number" value={form.value} onChange={e => set('value', e.target.value)} min="0"
                  className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 text-sm outline-none" style={{borderRadius:0}}
                  placeholder={form.type === 'percent' ? '10' : '50'} data-testid="coupon-value-input" />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Description</label>
              <input value={form.description} onChange={e => set('description', e.target.value)}
                className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 text-sm outline-none" style={{borderRadius:0}}
                placeholder="10% off your order" data-testid="coupon-desc-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 accent-primary" />
              <span className="text-sm text-white">Active</span>
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={save} disabled={saving}
                className="flex-1 bg-primary text-black font-bold uppercase text-xs py-2.5 hover:bg-white transition-colors disabled:opacity-60"
                data-testid="save-coupon-btn">
                {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
              </button>
              {editing && <button onClick={cancelEdit} className="border border-white/10 text-white/60 px-4 text-xs font-bold uppercase hover:text-white transition-colors">Cancel</button>}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-2">
          <div className="space-y-2">
            {coupons.length === 0 && <p className="text-muted-foreground text-sm">No coupons yet.</p>}
            {coupons.map(c => (
              <div key={c.code} className={`flex items-center justify-between bg-surface border px-4 py-3 ${c.active ? 'border-white/10' : 'border-white/5 opacity-50'}`} data-testid={`coupon-row-${c.code}`}>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-bold text-primary text-sm">{c.code}</span>
                  <span className="text-xs text-white/60">{c.description}</span>
                  <span className="text-xs bg-white/5 border border-white/10 px-2 py-0.5 text-white/60">
                    {c.type === 'percent' ? `${c.value}%` : c.type === 'fixed' ? `AED ${c.value}` : 'Free Ship'}
                  </span>
                  <span className={`text-xs font-bold ${c.active ? 'text-green-400' : 'text-red-400'}`}>{c.active ? 'ACTIVE' : 'OFF'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(c)} className="text-white/40 hover:text-primary transition-colors text-xs font-bold uppercase" data-testid={`toggle-coupon-${c.code}`}>{c.active ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => openEdit(c)} className="text-white/40 hover:text-primary transition-colors" data-testid={`edit-coupon-${c.code}`}><Edit size={13} /></button>
                  <button onClick={() => remove(c.code)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-coupon-${c.code}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function ChangePasswordSection({ token, getHeaders }) {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async () => {
    if (!form.current || !form.newPass || !form.confirm) {
      toast.error('Please fill in all fields'); return;
    }
    if (form.newPass !== form.confirm) {
      toast.error('New passwords do not match'); return;
    }
    if (form.newPass.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: form.current, new_password: form.newPass },
        { headers: getHeaders() }
      );
      toast.success('Password changed successfully');
      setForm({ current: '', newPass: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-heading font-bold text-lg uppercase text-white mb-6">Settings</h2>
      <div className="max-w-md bg-surface border border-white/10 p-6">
        <h3 className="font-bold text-sm uppercase tracking-widest text-white mb-5 flex items-center gap-2">
          <span className="w-1 h-4 bg-primary inline-block" /> Change Admin Password
        </h3>
        <div className="space-y-4">
          {[
            { key: 'current', label: 'Current Password', show: showCurrent, toggle: () => setShowCurrent(s => !s) },
            { key: 'newPass', label: 'New Password',     show: showNew,     toggle: () => setShowNew(s => !s) },
            { key: 'confirm', label: 'Confirm New Password', show: showNew, toggle: () => setShowNew(s => !s) },
          ].map(({ key, label, show, toggle }) => (
            <div key={key}>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2.5 pr-10 outline-none text-sm transition-colors"
                  style={{ borderRadius: 0 }}
                  data-testid={`pwd-${key}`}
                  placeholder="••••••••"
                />
                <button type="button" onClick={toggle}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition-colors text-xs">
                  {show ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 text-sm hover:bg-white transition-colors disabled:opacity-60 mt-2"
            data-testid="change-password-submit"
          >
            {saving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}


function ProductFormModal({ form, onClose, onSave, saving, token }) {
  const [data, setData] = useState(form);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  // Parse images to array for preview
  const imageList = (() => {
    if (!data.images) return [];
    if (Array.isArray(data.images)) return data.images.filter(Boolean);
    return data.images.split('\n').map(s => s.trim()).filter(Boolean);
  })();

  const setImageList = (list) => set('images', list.join('\n'));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload/image`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(res.data.url);
      }
      setImageList([...imageList, ...newUrls]);
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? 's' : ''} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (idx) => setImageList(imageList.filter((_, i) => i !== idx));

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="product-form-modal">
      <div className="bg-surface border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-surface z-10">
          <h2 className="font-heading font-bold text-lg uppercase text-white">{data.id ? 'Edit Product' : 'Add Product'}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[['name', 'Product Name *', 'text'], ['sku', 'SKU *', 'text'], ['brand', 'Brand *', 'text'], ['category', 'Category *', 'text'], ['price', 'Price (AED) *', 'number'], ['compare_price', 'Compare Price', 'number'], ['uae_market_price', 'UAE Market Price', 'number'], ['stock', 'Stock *', 'number'], ['voltage', 'Voltage', 'text'], ['warranty', 'Warranty', 'text']].map(([key, label, type]) => (
              <div key={key}>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</label>
                <input type={type} value={data[key] || ''} onChange={e => set(key, e.target.value)}
                  className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/20 px-3 py-2 outline-none text-sm transition-colors" style={{ borderRadius: 0 }} />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Power Type</label>
              <select value={data.power_type || 'corded'} onChange={e => set('power_type', e.target.value)} className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 outline-none text-sm" style={{ borderRadius: 0 }}>
                <option value="corded">Corded</option>
                <option value="cordless">Cordless</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!data.featured} onChange={e => set('featured', e.target.checked)} className="w-4 h-4 accent-primary" />
                <span className="text-sm text-white">Featured Product</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Description</label>
            <textarea value={data.description || ''} onChange={e => set('description', e.target.value)} rows={2} className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 outline-none text-sm resize-none transition-colors" style={{ borderRadius: 0 }} />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Product Images</label>

            {/* Upload Button */}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              data-testid="image-file-input"
              onChange={handleFileUpload}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-image-btn"
              className="flex items-center gap-2 border border-dashed border-white/20 hover:border-primary text-white/60 hover:text-primary px-4 py-3 text-sm font-bold uppercase tracking-wider transition-all w-full justify-center mb-3 disabled:opacity-50"
              style={{ borderRadius: 0 }}
            >
              <Upload size={15} />
              {uploading ? 'Uploading...' : 'Upload Images from Computer'}
            </button>

            {/* Image Previews */}
            {imageList.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {imageList.map((url, idx) => (
                  <div key={idx} className="relative group w-20 h-20 border border-white/10 bg-black/30 overflow-hidden flex-shrink-0">
                    <img
                      src={getImageSrc(url)}
                      alt={`img-${idx}`}
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div className="hidden w-full h-full items-center justify-center bg-black/50">
                      <ImageIcon size={20} className="text-white/30" />
                    </div>
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${idx}`}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* URL Input (paste external URLs) */}
            <div>
              <p className="text-xs text-white/30 mb-1">Or paste image URLs (one per line):</p>
              <textarea
                value={typeof data.images === 'string' ? data.images : (data.images || []).join('\n')}
                onChange={e => set('images', e.target.value)}
                rows={2}
                className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/20 px-3 py-2 outline-none text-sm resize-none transition-colors"
                style={{ borderRadius: 0 }}
                placeholder="https://example.com/image.jpg"
                data-testid="image-urls-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Features (one per line)</label>
            <textarea value={typeof data.features === 'string' ? data.features : (data.features || []).join('\n')} onChange={e => set('features', e.target.value)} rows={2} className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/20 px-3 py-2 outline-none text-sm resize-none transition-colors" style={{ borderRadius: 0 }} placeholder="Feature 1&#10;Feature 2..." />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Tags (comma separated)</label>
            <input value={typeof data.tags === 'string' ? data.tags : (data.tags || []).join(', ')} onChange={e => set('tags', e.target.value)} className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/20 px-3 py-2 outline-none text-sm transition-colors" style={{ borderRadius: 0 }} placeholder="drill, cordless, 18v" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Specs (JSON format)</label>
            <textarea value={typeof data.specs === 'object' ? JSON.stringify(data.specs, null, 2) : (data.specs || '{}')} onChange={e => set('specs', e.target.value)} rows={4} className="w-full bg-background border border-white/10 focus:border-primary text-white px-3 py-2 outline-none text-xs font-mono resize-none transition-colors" style={{ borderRadius: 0 }} />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => onSave(data)} disabled={saving}
              className="flex-1 bg-primary text-black font-bold uppercase tracking-wider py-3 text-sm hover:bg-white transition-colors disabled:opacity-60"
              data-testid="save-product-btn">
              {saving ? 'Saving...' : (data.id ? 'Update Product' : 'Create Product')}
            </button>
            <button onClick={onClose} className="border border-white/20 text-white/60 hover:text-white px-6 py-3 text-sm transition-colors font-bold uppercase">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
